import {
  useMutation,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query'

import type {
  AvailablePhoneNumber,
  CreatePbxLocationRequest,
  PbxLocation,
  PbxLocationDeleteRequiresReassignmentError,
  UpdatePbxLocationRequest,
} from '@mangovoice/types'

import type {
  ApiError,
  DeleteLocationError,
  PaginatedApiResult,
  PbxLocationsApi,
} from '../api/pbxLocationsApi.types'

export const PbxLocationsQueryKeysEnum = {
  PBX_LOCATIONS: 'pbxLocations',
  AVAILABLE_PHONE_NUMBERS: 'pbxLocationAvailablePhoneNumbers',
} as const

interface UseGetPbxLocationsQueryParams {
  api: PbxLocationsApi
  pbxId: number | undefined
  search?: string
  ordering?: string
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<PbxLocation>, ApiError>,
    'queryKey'
  >
}

export const useGetPbxLocationsQuery = ({
  api,
  pbxId,
  search,
  ordering,
  options,
}: UseGetPbxLocationsQueryParams) => {
  return useQuery<PaginatedApiResult<PbxLocation>, ApiError>({
    queryKey: [
      PbxLocationsQueryKeysEnum.PBX_LOCATIONS,
      { pbxId, search, ordering },
    ],
    queryFn: async () => {
      if (!pbxId) {
        return Promise.reject<PaginatedApiResult<PbxLocation>>({
          message: 'pbxId is required',
        })
      }
      const response = await api.getPbxLocations({
        pbxId,
        search,
        ordering,
      })
      if (!response.ok) {
        return Promise.reject({
          ...(response.data ?? {}),
          status: response.status,
        })
      }
      return response.data as PaginatedApiResult<PbxLocation>
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: options?.retry ?? 1,
  })
}

interface CreatePbxLocationHandlers {
  onSuccess?: (location: PbxLocation) => void
  onError?: (error: ApiError) => void
  onCompletion?: () => void
}

export const useCreatePbxLocationMutation = (
  api: PbxLocationsApi,
  handlers?: CreatePbxLocationHandlers,
) => {
  return useMutation<PbxLocation, ApiError, CreatePbxLocationRequest>({
    mutationFn: async (body) => {
      const response = await api.createPbxLocation(body)
      if (!response.ok) {
        return Promise.reject({
          ...(response.data ?? {}),
          status: response.status,
        } as ApiError)
      }
      return response.data as PbxLocation
    },
    onSuccess: handlers?.onSuccess,
    onError: handlers?.onError,
    onSettled: handlers?.onCompletion,
    retry: 0,
  })
}

interface UpdatePbxLocationVariables {
  id: number
  body: UpdatePbxLocationRequest
}

interface UpdatePbxLocationHandlers {
  onSuccess?: (location: PbxLocation) => void
  onError?: (error: ApiError) => void
  onCompletion?: () => void
}

export const useUpdatePbxLocationMutation = (
  api: PbxLocationsApi,
  handlers?: UpdatePbxLocationHandlers,
) => {
  return useMutation<PbxLocation, ApiError, UpdatePbxLocationVariables>({
    mutationFn: async ({ id, body }) => {
      const response = await api.updatePbxLocation(id, body)
      if (!response.ok) {
        return Promise.reject({
          ...(response.data ?? {}),
          status: response.status,
        } as ApiError)
      }
      return response.data as PbxLocation
    },
    onSuccess: handlers?.onSuccess,
    onError: handlers?.onError,
    onSettled: handlers?.onCompletion,
    retry: 0,
  })
}

/**
 * Error variant emitted by the delete mutation when the API returns 400 with
 * `requires_reassignment: true` — caller should prompt for a destination
 * location and re-call the mutation with `newLocationId` set.
 */
export type DeletePbxLocationReassignmentRequiredError = ApiError &
  PbxLocationDeleteRequiresReassignmentError

export const isDeletePbxLocationReassignmentRequiredError = (
  error: ApiError | DeleteLocationError | undefined,
): error is DeletePbxLocationReassignmentRequiredError => {
  if (!error) return false
  return (
    (error as DeletePbxLocationReassignmentRequiredError)
      .requires_reassignment === true
  )
}

interface DeletePbxLocationVariables {
  id: number
  newLocationId?: number
}

interface DeletePbxLocationHandlers {
  onSuccess?: (variables: DeletePbxLocationVariables) => void
  onError?: (
    error: ApiError | DeletePbxLocationReassignmentRequiredError,
    variables: DeletePbxLocationVariables,
  ) => void
  onCompletion?: () => void
}

export const useDeletePbxLocationMutation = (
  api: PbxLocationsApi,
  handlers?: DeletePbxLocationHandlers,
) => {
  return useMutation<
    DeletePbxLocationVariables,
    ApiError | DeletePbxLocationReassignmentRequiredError,
    DeletePbxLocationVariables
  >({
    mutationFn: async ({ id, newLocationId }) => {
      const response = await api.deletePbxLocation(id, newLocationId)
      if (!response.ok) {
        return Promise.reject({
          ...(response.data ?? {}),
          status: response.status,
        })
      }
      return { id, newLocationId }
    },
    onSuccess: (variables) => {
      handlers?.onSuccess?.(variables)
    },
    onError: (error, variables) => {
      handlers?.onError?.(error, variables)
    },
    onSettled: handlers?.onCompletion,
    retry: 0,
  })
}

interface UseGetAvailablePhoneNumbersQueryParams {
  api: PbxLocationsApi
  pbxId: number | undefined
  excludeLocationId?: number
  options?: Omit<UseQueryOptions<AvailablePhoneNumber[], ApiError>, 'queryKey'>
}

export const useGetAvailablePhoneNumbersQuery = ({
  api,
  pbxId,
  excludeLocationId,
  options,
}: UseGetAvailablePhoneNumbersQueryParams) => {
  return useQuery<AvailablePhoneNumber[], ApiError>({
    queryKey: [
      PbxLocationsQueryKeysEnum.AVAILABLE_PHONE_NUMBERS,
      { pbxId, excludeLocationId },
    ],
    queryFn: async () => {
      if (!pbxId) {
        return Promise.reject<AvailablePhoneNumber[]>({
          message: 'pbxId is required',
        })
      }
      const response = await api.getAvailablePhoneNumbersForLocation({
        pbxId,
        excludeLocationId,
      })
      if (!response.ok) {
        return Promise.reject({
          ...(response.data ?? {}),
          status: response.status,
        })
      }
      return response.data ?? []
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: options?.retry ?? 1,
  })
}
